import { useCallback, useEffect, useState } from 'react';

import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

import { getPremiumFunnel, type PremiumFunnelResult } from './paymentOverviewApi';

function nextUtcDay(date: string) {
    const value = new Date(`${date}T00:00:00.000Z`);
    value.setUTCDate(value.getUTCDate() + 1);
    return value.toISOString();
}

function label(value: string) {
    if (value === 'ai_score_result') return 'AI Score Result';
    return value.replaceAll('_', ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatDateTime(value: string) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function Metric({ title, events, users, rate }: { title: string; events: number; users: number; rate?: string }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, minWidth: 0, borderRadius: 2 }}>
            <Typography variant="overline" color="text.secondary">{title}</Typography>
            <Typography variant="h4" fontWeight={800}>{users.toLocaleString()}</Typography>
            <Typography variant="body2" color="text.secondary">
                unique visitors · {events.toLocaleString()} total events{rate ? ` · ${rate}` : ''}
            </Typography>
        </Paper>
    );
}

export function PremiumFunnelPanel({ startDate, endDate }: { startDate: string; endDate: string }) {
    const [data, setData] = useState<PremiumFunnelResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            setData(await getPremiumFunnel(`${startDate}T00:00:00.000Z`, nextUtcDay(endDate)));
        } catch (reason) {
            setData(null);
            setError(reason instanceof Error ? reason.message : 'Unable to load premium funnel.');
        } finally {
            setLoading(false);
        }
    }, [endDate, startDate]);

    useEffect(() => { void load(); }, [load]);

    if (loading && !data) return <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>;
    if (error) return <Alert severity="error" action={<Button color="inherit" onClick={() => void load()}>Retry</Button>}>{error}</Alert>;
    if (!data) return null;

    const summary = data.summary;
    return (
        <Stack gap={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography variant="h6" fontWeight={800}>Premium conversion funnel</Typography>
                    <Typography variant="body2" color="text.secondary">Every card shows distinct browser/app visitors first and total clicks/views second.</Typography>
                </Box>
                <Button startIcon={loading ? <CircularProgress size={15} /> : <RefreshIcon />} onClick={() => void load()} disabled={loading}>Refresh</Button>
            </Stack>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                <Metric title="Go Premium clicks" events={summary.ctaClicks} users={summary.ctaUsers} />
                <Metric title="Premium page landings" events={summary.premiumPageViews} users={summary.premiumPageUsers} rate={`${summary.ctaToPageRate}% from CTA`} />
                <Metric title="Plan clicks" events={summary.planClicks} users={summary.planUsers} rate={`${summary.pageToPlanRate}% from page`} />
                <Metric title="Checkout starts" events={summary.checkoutStarts} users={summary.checkoutUsers} rate={`${summary.planToCheckoutRate}% from plan`} />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(0, 1.4fr)' }, gap: 2 }}>
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ p: 2 }}>Go Premium clicks by placement</Typography>
                    <TableContainer><Table size="small"><TableHead><TableRow><TableCell>Placement</TableCell><TableCell align="right">Users</TableCell><TableCell align="right">Clicks</TableCell></TableRow></TableHead><TableBody>
                        {data.ctaSources.map((source) => <TableRow key={source.source}><TableCell>{label(source.source)}</TableCell><TableCell align="right">{source.users}</TableCell><TableCell align="right">{source.clicks}</TableCell></TableRow>)}
                        {!data.ctaSources.length ? <TableRow><TableCell colSpan={3} align="center">No CTA clicks</TableCell></TableRow> : null}
                    </TableBody></Table></TableContainer>
                </Paper>

                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ p: 2 }}>Plan interest and checkout starts</Typography>
                    <TableContainer><Table size="small"><TableHead><TableRow><TableCell>Plan</TableCell><TableCell align="right">Users</TableCell><TableCell align="right">Clicks</TableCell><TableCell align="right">Checkout users</TableCell></TableRow></TableHead><TableBody>
                        {data.plans.map((plan) => <TableRow key={plan.planId || plan.planName}><TableCell><Typography variant="body2" fontWeight={700}>{plan.planName}</Typography><Typography variant="caption" color="text.secondary">{plan.durationDays ? `${plan.durationDays} days · ` : ''}{plan.currency}</Typography></TableCell><TableCell align="right">{plan.users}</TableCell><TableCell align="right">{plan.clicks}</TableCell><TableCell align="right">{plan.checkoutUsers}</TableCell></TableRow>)}
                        {!data.plans.length ? <TableRow><TableCell colSpan={4} align="center">No plan clicks</TableCell></TableRow> : null}
                    </TableBody></Table></TableContainer>
                </Paper>
            </Box>

            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={800} sx={{ p: 2 }}>Recent premium activity</Typography>
                <TableContainer><Table size="small" sx={{ minWidth: 1180 }}><TableHead><TableRow><TableCell>Time</TableCell><TableCell>User</TableCell><TableCell>Action</TableCell><TableCell>Stage / outcome</TableCell><TableCell>Evidence</TableCell><TableCell>Plan</TableCell><TableCell>Platform</TableCell></TableRow></TableHead><TableBody>
                    {data.recentEvents.map((event) => <TableRow key={event.eventId}><TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(event.occurredAtUtc)}</TableCell><TableCell><Typography variant="body2" fontWeight={700}>{event.userName}</Typography><Typography variant="caption" color="text.secondary">{event.email}</Typography></TableCell><TableCell>{label(event.eventName)}</TableCell><TableCell><Typography variant="body2">{label(event.stage || event.source)}</Typography><Typography variant="caption" color={event.outcome === 'failed' || event.outcome === 'blocked' ? 'error.main' : 'text.secondary'}>{label(event.outcome || 'recorded')}</Typography></TableCell><TableCell><Typography variant="caption" color="error.main">{event.errorCode ? label(event.errorCode) : '—'}</Typography><Typography variant="caption" display="block" sx={{ fontFamily: 'monospace' }}>{event.paymentIntentId || event.correlationId || '—'}</Typography></TableCell><TableCell>{event.planName || '—'}</TableCell><TableCell>{label(event.platform || 'unknown')}</TableCell></TableRow>)}
                    {!data.recentEvents.length ? <TableRow><TableCell colSpan={7} align="center">No premium activity in this period.</TableCell></TableRow> : null}
                </TableBody></Table></TableContainer>
            </Paper>
        </Stack>
    );
}
